// @flow

const ACTIVE_STATUSES = ['new', 'acknowledged', 'assigned', 'contacted', 'nurturing'];
const TERMINAL_STATUSES = ['won', 'lost', 'closed'];
const CONTACTABLE_STATUSES = ['new', 'acknowledged', 'assigned'];

type ContactRequestRecordLike = {
    record: {
        id: string,
        name: string,
        email: string,
        message: string,
        source: string,
        created_at: number,
        status: string,
        acknowledged_at?: ?number,
        assignee_name?: ?string,
        assignee_email?: ?string,
        assigned_admin_id?: ?string,
        last_activity_at?: ?number,
        next_follow_up_at?: ?number,
        last_contacted_at?: ?number,
    },
};

type AdminLike = {
    email?: ?string,
    first_name?: ?string,
    last_name?: ?string,
};

export function getActiveStatuses(): string[] {
    return ACTIVE_STATUSES;
}

export function getTerminalStatuses(): string[] {
    return TERMINAL_STATUSES;
}

export function formatTimestamp(ts: ?number): string {
    if (!ts) {
        return 'Not set';
    }

    return new Date(ts * 1000).toLocaleString();
}

export function toDateInputValue(ts: ?number): string {
    if (!ts) {
        return '';
    }

    return new Date(ts * 1000).toISOString().slice(0, 10);
}

export function fromDateInputValue(value: string): ?number {
    if (!value) {
        return null;
    }

    return Math.floor(new Date(`${value}T12:00:00Z`).getTime() / 1000);
}

export function getStatusColor(status: string): string {
    switch (status) {
        case 'new':
            return 'secondary';
        case 'acknowledged':
            return 'info';
        case 'assigned':
            return 'primary';
        case 'contacted':
            return 'warning';
        case 'nurturing':
            return 'dark';
        case 'won':
            return 'success';
        case 'lost':
            return 'danger';
        case 'closed':
            return 'secondary';
        default:
            return 'light';
    }
}

export function getAdminDisplayName(admin: ?AdminLike): string {
    if (!admin) {
        return '';
    }

    return [admin.first_name, admin.last_name].filter(Boolean).join(' ') || admin.email || '';
}

export function isAssigned(request: ContactRequestRecordLike): boolean {
    return Boolean(request.record.assignee_email || request.record.assigned_admin_id);
}

export function isTerminal(request: ContactRequestRecordLike): boolean {
    return TERMINAL_STATUSES.includes(request.record.status);
}

export function needsAttention(request: ContactRequestRecordLike): boolean {
    const now = Math.floor(Date.now() / 1000);

    if (isTerminal(request)) {
        return false;
    }

    if (!request.record.acknowledged_at) {
        return true;
    }

    if (!isAssigned(request)) {
        return true;
    }

    if (request.record.next_follow_up_at && request.record.next_follow_up_at <= now) {
        return true;
    }

    if (request.record.last_activity_at) {
        return request.record.last_activity_at <= now - 7 * 24 * 60 * 60;
    }

    return false;
}

export function getRequestPreview(message: string): string {
    const normalized = message.replace(/\s+/g, ' ').trim();
    if (normalized.length <= 140) {
        return normalized;
    }

    return `${normalized.slice(0, 137)}...`;
}

export function buildQuickReplySubject(request: ContactRequestRecordLike): string {
    return `BillMaster follow-up for ${request.record.name}`;
}

export function buildQuickReplyBody(request: ContactRequestRecordLike, currentAdmin: ?AdminLike): string {
    const adminName = getAdminDisplayName(currentAdmin) || 'BillMaster team';

    return [
        `Hi ${request.record.name},`,
        '',
        'Thanks for reaching out to BillMaster. I am following up on your request and would be happy to help with next steps.',
        '',
        'If you would rather schedule time together, I can send you a calendar invite as well.',
        '',
        'Best,',
        adminName,
    ].join('\n');
}

export function buildAssignToCurrentAdminPatch(currentAdmin: ?AdminLike): ?{ assignee_name: string, assignee_email: string } {
    if (!currentAdmin || !currentAdmin.email) {
        return null;
    }

    return {
        assignee_name: getAdminDisplayName(currentAdmin) || currentAdmin.email,
        assignee_email: currentAdmin.email,
    };
}

export function buildQuickReplyUpdate(currentAdmin: ?AdminLike, now: number = Math.floor(Date.now() / 1000)): Object {
    const body = {
        status: 'contacted',
        last_contacted_at: now,
    };

    const assigneePatch = buildAssignToCurrentAdminPatch(currentAdmin);
    if (assigneePatch) {
        body.assignee_name = assigneePatch.assignee_name;
        body.assignee_email = assigneePatch.assignee_email;
    }

    return body;
}

function encodeUrl(baseUrl: string, params: { [string]: string }): string {
    const search = new URLSearchParams(params);
    return `${baseUrl}?${search.toString()}`;
}

function toGoogleCalendarDate(date: Date): string {
    return date.toISOString().replace(/[-:]|\.\d{3}/g, '');
}

function getDefaultCalendarWindow(): { start: Date, end: Date } {
    const start = new Date();
    start.setMinutes(0, 0, 0);
    start.setHours(start.getHours() + 1);

    const end = new Date(start.getTime() + 30 * 60 * 1000);
    return { start, end };
}

export function buildGmailComposeUrl(
    request: ContactRequestRecordLike,
    subject: string,
    body: string,
): string {
    return encodeUrl('https://mail.google.com/mail/', {
        view: 'cm',
        fs: '1',
        to: request.record.email,
        su: subject,
        body,
    });
}

export function buildGoogleCalendarUrl(
    request: ContactRequestRecordLike,
    currentAdmin: ?AdminLike,
    body: string,
): string {
    const { start, end } = getDefaultCalendarWindow();
    const adminName = getAdminDisplayName(currentAdmin) || 'BillMaster';
    const details = [
        `Lead: ${request.record.name}`,
        `Email: ${request.record.email}`,
        `Source: ${request.record.source}`,
        `Contact Request ID: ${request.record.id}`,
        '',
        body,
        '',
        `Owner: ${adminName}`,
    ].join('\n');

    return encodeUrl('https://calendar.google.com/calendar/render', {
        action: 'TEMPLATE',
        text: `BillMaster follow-up call: ${request.record.name}`,
        add: request.record.email,
        dates: `${toGoogleCalendarDate(start)}/${toGoogleCalendarDate(end)}`,
        details,
    });
}

export function getVisibleQuickActions(request: ContactRequestRecordLike, currentAdmin: ?AdminLike): string[] {
    const actions = ['details'];

    if (isTerminal(request)) {
        return actions;
    }

    if (!request.record.acknowledged_at) {
        actions.unshift('acknowledge');
    }

    if (!isAssigned(request) || request.record.assignee_email !== currentAdmin?.email) {
        actions.push('assign-to-me');
    }

    if (CONTACTABLE_STATUSES.includes(request.record.status) && !request.record.last_contacted_at) {
        actions.push('mark-contacted');
    }

    actions.push('close');
    actions.push('reply');
    actions.push('schedule-call');
    return actions;
}

export function buildInitialDraft(request: ContactRequestRecordLike, currentAdmin: ?AdminLike) {
    return {
        status: request.record.status || 'new',
        assigneeName: request.record.assignee_name || '',
        assigneeEmail: request.record.assignee_email || '',
        notes: request.record.notes || '',
        nextFollowUpAt: toDateInputValue(request.record.next_follow_up_at),
        replySubject: buildQuickReplySubject(request),
        replyBody: buildQuickReplyBody(request, currentAdmin),
    };
}